import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e2) {
      setErr(apiError(e2));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md pc-reveal">
        <Link to="/login" className="flex items-center gap-3 mb-8" aria-label="PropertyConnect home">
          <BrandLogo className="h-[88px]" />
        </Link>

        {sent ? (
          <div data-testid="forgot-success">
            <div className="h-12 w-12 rounded-2xl border border-accent/40 flex items-center justify-center mb-5">
              <MailCheck className="h-6 w-6 text-accent" />
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Check your email</h1>
            <p className="text-slate-500 mt-2">If an account exists for <span className="font-medium text-slate-700">{email}</span>, we've sent password reset instructions. Please also check your spam folder.</p>
            <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-accent font-semibold hover:underline" data-testid="back-to-login">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Reset your password</h1>
            <p className="text-slate-500 mt-2">Enter the email associated with your account and we'll send you instructions to reset your password.</p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <Input id="forgot-email" data-testid="forgot-email" type="email" autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" className="mt-1.5 h-11" required />
              </div>
              {err && <p data-testid="forgot-error" role="alert" className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{err}</p>}
              <Button data-testid="forgot-submit" type="submit" disabled={busy} className="w-full h-11 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-xl">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset instructions"}
              </Button>
            </form>
            <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors" data-testid="back-to-login">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </>
        )}
        <SiteFooter variant="minimal" />
      </div>
    </div>
  );
}
